import { FC, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryParam } from '@datassential/platform-ui-lib';
import { useCountrySwitch } from '@hooks/useCountrySwitch';
import {
    CATEGORIES_IDS_PARAM,
    CHAIN_NAME_PARAM,
    COUNTRY_ID_PARAM,
    CountryId,
    TARGET_PAGE_PARAM
} from '@core/constants';
import { Route } from '@core/routes/Route';
import { getVariantByRoute, useFiltersContext } from '@core/FiltersContext';
import { FiltersVariant, iFilterValue } from '@models/Filter';

const ApplyFilters: FC = () => {
    const history = useNavigate();

    const chainName = useQueryParam(CHAIN_NAME_PARAM);
    const categoriesIdsStr = useQueryParam(CATEGORIES_IDS_PARAM);
    const countryId = useQueryParam(COUNTRY_ID_PARAM) || CountryId.USA;
    const route: Route = decodeURIComponent(useQueryParam(TARGET_PAGE_PARAM)) as Route || Route.Home;

    const filterVariant = useMemo(
        () => getVariantByRoute(route) || FiltersVariant.Homepage,
        [route]
    );

    const categoriesIds = useMemo(
        () => categoriesIdsStr === ''
            ? []
            : categoriesIdsStr.split(',').map(id => Number(id)),
        [categoriesIdsStr]
    );

    const { shouldSwitchCountry, switchCountry } = useCountrySwitch();
    const { isLoaded, updateSpecificValueByChainName, updateSpecificValue } = useFiltersContext();

    const otherFilters = useMemo(
        (): Partial<Omit<iFilterValue, 'chainId' | 'search' | 'additionalSearch'>> => ({
            categories: categoriesIds,
        }),
        [categoriesIds]
    );

    const launchChain = useCallback(
        () => {
            if (shouldSwitchCountry(countryId)) {
                switchCountry(countryId);
            } else {
                updateSpecificValueByChainName(filterVariant, chainName, otherFilters);
                history(route);
            }
        },
        [
            chainName,
            countryId,
            shouldSwitchCountry,
            history,
            updateSpecificValueByChainName,
            switchCountry,
            otherFilters,
            filterVariant,
            route,
        ]
    );

    const applyOtherFilters = useCallback(
        () => {
            updateSpecificValue(filterVariant, otherFilters);
            history(route);
        },
        [
            otherFilters,
            updateSpecificValue,
            history,
            filterVariant,
            route,
        ]
    );

    useEffect(() => {
        if (!isLoaded) return;

        if (chainName) launchChain();
        else applyOtherFilters();
    }, [chainName, isLoaded, launchChain, applyOtherFilters]);

    return null;
};

export default ApplyFilters;
